import { useMathLibrary, useSaved } from '@echo/core/react';
import { Button } from '@echo/ui-web';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, useCanGoBack, useNavigate, useRouter } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, Heart } from 'lucide-react';
import { MathHeading } from '@/components/library-items';
import { mathArt } from '@/lib/math-art';

export const Route = createFileRoute('/_app/library_/math/set/$lineageUuid')({
  component: MathInfoPage,
});

/**
 * Library › Math › a row's info (Figma library, 3530:112415): what the row practises before
 * starting it. The row, its summary, "Practice now" (through the coach, on the row's next sheet)
 * and "Favorite", which keeps it in Library › Math › My favorites; the heart in the toolbar
 * does the same.
 */
function MathInfoPage() {
  const { lineageUuid } = Route.useParams();
  const { t } = useLingui();
  const navigate = useNavigate();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const math = useMathLibrary();
  const saved = useSaved();
  const entry = math.entryFor(lineageUuid);
  // Saved may hold any lineage that opens the row.
  const isSaved = entry?.lineages.some(saved.isSaved) === true;

  const back = () =>
    canGoBack ? router.history.back() : navigate({ to: '/library', search: { shelf: 'math' } });
  // A row chosen here is already the choice: straight on to the coach.
  const start = () =>
    void navigate({
      to: '/exercise/$kind/intro',
      params: { kind: 'math' },
      state: { exercise: { source: 'workbook', mathSet: entry?.lineageUuid ?? lineageUuid } },
    });
  const toggle = () => entry && saved.toggle(entry);

  return (
    <div className="flex w-full flex-col gap-content-sm">
      <div className="flex min-h-tap-target items-center justify-between gap-content-sm">
        <Button variant="ghost" onClick={back}>
          <ArrowLeft size={24} aria-hidden />
          {t`Back`}
        </Button>
        {entry && saved.available ? (
          <button
            type="button"
            onClick={toggle}
            aria-pressed={isSaved}
            aria-label={isSaved ? t`Remove favorite` : t`Favorite`}
            className="flex size-tap-target cursor-pointer items-center justify-center rounded-full bg-surface text-on-action-secondary shadow-task-row focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-highlight"
          >
            <Heart size={24} aria-hidden fill={isSaved ? 'currentColor' : 'none'} />
          </button>
        ) : null}
      </div>

      {!math.ready ? (
        <div className="flex justify-center py-section-sm">
          <span
            aria-hidden
            className="size-10 animate-spin rounded-full border-4 border-action-primary border-t-transparent"
          />
        </div>
      ) : !entry ? (
        <p className="text-body-md text-on-surface-medium">
          <Trans>There's nothing here yet.</Trans>
        </p>
      ) : (
        <section className="flex flex-col gap-content-lg rounded-xl border border-border-weak bg-surface px-content-sm py-content-md">
          <MathHeading
            title={entry.title}
            byline={entry.byline}
            tags={entry.tags}
            art={{ src: mathArt(entry) }}
          />
          {entry.summary ? (
            <p className="text-body-md text-on-surface-medium">{entry.summary}</p>
          ) : null}
          <div className="flex flex-col gap-content-sm">
            <Button onClick={start}>
              {t`Practice now`}
              <ArrowRight size={24} aria-hidden />
            </Button>
            {saved.available ? (
              <Button variant="tertiary" aria-pressed={isSaved} onClick={toggle}>
                <Heart size={24} aria-hidden fill={isSaved ? 'currentColor' : 'none'} />
                {isSaved ? t`Remove favorite` : t`Favorite`}
              </Button>
            ) : null}
          </div>
        </section>
      )}
    </div>
  );
}
